// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Selectors over a site's HITL plan approvals. Home › Recommendations and its
// tab badge both read these, so the list an operator sees and the number on the
// tab are derived from one definition and can't drift apart.

import {
  executesToday,
  executesTodayOrTomorrow,
  executesTodayOrLater,
} from '../utils/planAttributes';
import { isExecuting } from '../utils/planLifecycle';
import type { HitlApproval } from '../types';

/** How far back a plan can have been raised and still count as imminent. */
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/** Creation time in ms for sorting; missing/invalid dates sort last (0). */
export function createdAtMs(createdAt: string | null | undefined): number {
  if (!createdAt) return 0;
  const ms = new Date(createdAt).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * The operator's imminent queue: plans executing today or tomorrow that were
 * raised in the last 7 days, newest first.
 */
export function imminentPlans(
  approvals: HitlApproval[],
  now: Date = new Date(),
  timeZone?: string
): HitlApproval[] {
  const cutoff = now.getTime() - SEVEN_DAYS_MS;
  return approvals
    .filter(
      a =>
        (a.state === 'APPROVED' || a.state === 'REJECTED') &&
        createdAtMs(a.createdAt) >= cutoff &&
        executesTodayOrTomorrow(a.attributes, now, timeZone)
    )
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
}

/**
 * Badge count for the operator's Recommendations tab: how many *approved* plans
 * execute today or tomorrow. Rejected plans render in the list but aren't work,
 * so they're left out of the number.
 */
export function countImminentApproved(
  approvals: HitlApproval[],
  now: Date = new Date(),
  timeZone?: string
): number {
  return imminentPlans(approvals, now, timeZone).filter(a => a.state === 'APPROVED').length;
}

/**
 * The archive on Home › Closed: plans that have been decided, newest-raised
 * first, with no execution-date or creation-age bound — everything the shared
 * approvals window holds.
 */
export function decidedPlans(
  approvals: HitlApproval[],
  { includeRejected = false }: { includeRejected?: boolean } = {}
): HitlApproval[] {
  return approvals
    .filter(a => a.state === 'APPROVED' || (includeRejected && a.state === 'REJECTED'))
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
}

/**
 * The Ops Manager's review queue: plans still awaiting a decision that execute
 * today or later, newest-raised first.
 */
export function pendingReviewPlans(
  approvals: HitlApproval[],
  now: Date = new Date(),
  timeZone?: string
): HitlApproval[] {
  return approvals
    .filter(a => a.state === 'PENDING' && executesTodayOrLater(a.attributes, now, timeZone))
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
}

/**
 * Everything that isn't awaiting review — the complement of
 * {@link pendingReviewPlans}. The Recommendations tabs partition every plan
 * exactly once across pendingReviewPlans / executingPlans / pastPlans, with
 * executingPlans ⊂ resolvedPlans: a plan running now shows under Live, not Past.
 */
export function resolvedPlans(
  approvals: HitlApproval[],
  now: Date = new Date(),
  timeZone?: string
): HitlApproval[] {
  return approvals
    .filter(a => !(a.state === 'PENDING' && executesTodayOrLater(a.attributes, now, timeZone)))
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
}

/**
 * The Live tab: approved plans whose execution window contains `now`, newest
 * first.
 */
export function executingPlans(approvals: HitlApproval[], now: Date = new Date()): HitlApproval[] {
  return approvals
    .filter(a => isExecuting(a, now))
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
}

/**
 * The Past recommendations tab: {@link resolvedPlans} minus whatever is
 * executing right now, so one plan is never listed as both live and past.
 * `timeZone` must be the one {@link pendingReviewPlans} is given: with different
 * cut-offs a plan can fall in neither tab.
 */
export function pastPlans(
  approvals: HitlApproval[],
  now: Date = new Date(),
  timeZone?: string
): HitlApproval[] {
  const live = new Set(executingPlans(approvals, now).map(a => a.approvalId));
  return resolvedPlans(approvals, now, timeZone).filter(a => !live.has(a.approvalId));
}

/**
 * The Live tab's fallback when nothing is executing: the plan raised for the
 * site's today, whatever became of it, newest-raised first.
 */
export function todaysPlanApproval(
  approvals: HitlApproval[],
  now: Date = new Date(),
  timeZone?: string
): HitlApproval | undefined {
  return approvals
    .filter(a => !isExecuting(a, now) && executesToday(a.attributes, now, timeZone))
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt))[0];
}

/** How a plan on the Past tab ended: decided, or never decided before its day passed. */
export type PastOutcome = 'approved' | 'rejected' | 'expired';

/** Filter value on the Past tab: one outcome, or every plan. */
export type PastFilter = 'all' | PastOutcome;

/**
 * The outcome of a plan listed on Past. A pending plan only reaches Past once
 * its execution day has passed (see {@link pendingReviewPlans}), so pending
 * there means nobody reviewed it in time: expired, not "Pending approval".
 */
export function pastOutcome(approval: HitlApproval): PastOutcome {
  if (approval.state === 'APPROVED') return 'approved';
  if (approval.state === 'REJECTED') return 'rejected';
  return 'expired';
}

/** How many Past plans each filter would show, for the chip counts. */
export function pastOutcomeCounts(plans: HitlApproval[]): Record<PastFilter, number> {
  const counts: Record<PastFilter, number> = {
    all: plans.length,
    approved: 0,
    rejected: 0,
    expired: 0,
  };
  plans.forEach(a => {
    counts[pastOutcome(a)] += 1;
  });
  return counts;
}

/** Past plans for one filter, ordered by when they were raised (newest first by default). */
export function filterAndSortPast(
  plans: HitlApproval[],
  filter: PastFilter = 'all',
  order: 'newest' | 'oldest' = 'newest'
): HitlApproval[] {
  const kept = filter === 'all' ? plans.slice() : plans.filter(a => pastOutcome(a) === filter);
  const dir = order === 'oldest' ? 1 : -1;
  return kept.sort((a, b) => dir * (createdAtMs(a.createdAt) - createdAtMs(b.createdAt)));
}
